// src/lib/opportunity/opportunitySignedDocumentGate.ts

import type { OpportunityFormatKey } from "../../types/opportunity.js";
import {
  selectFormatDocumentSlots,
  type FormatDocumentSlot,
} from "./formatDocumentSlotTypes.js";
import {
  formatMissingRequiredDocumentSlotsError,
  missingRequiredDocumentSlots,
  type MissingRequiredDocumentSlot,
} from "./requiredDocumentSlots.js";

const SCHEDULE_READY_STATUSES = new Set(["signed", "ordered", "in_process", "complete"]);

export function isScheduleReadyOpportunityStatus(status: string): boolean {
  return SCHEDULE_READY_STATUSES.has(status);
}

export type SignedDocumentGateResult =
  | { ok: true }
  | { ok: false; error: string; missing_slots: MissingRequiredDocumentSlot[] };

export function shouldEnforceRequiredDocumentsOnStatusChange(args: {
  previousStatus: string;
  nextStatus: string;
}): boolean {
  return (
    isScheduleReadyOpportunityStatus(args.nextStatus) &&
    !isScheduleReadyOpportunityStatus(args.previousStatus)
  );
}

export function checkRequiredDocumentsBeforeScheduleReady(args: {
  previousStatus: string;
  nextStatus: string;
  productType: string;
  formatKey: OpportunityFormatKey;
  slots: FormatDocumentSlot[];
  documents: { category: string }[];
}): SignedDocumentGateResult {
  if (!shouldEnforceRequiredDocumentsOnStatusChange(args)) {
    return { ok: true };
  }

  const slotDefs = selectFormatDocumentSlots({
    slots: args.slots,
    productType: args.productType,
    formatKey: args.formatKey,
  });
  const missing = missingRequiredDocumentSlots({ slotDefs, documents: args.documents });
  if (missing.length === 0) return { ok: true };

  return {
    ok: false,
    error: formatMissingRequiredDocumentSlotsError(missing),
    missing_slots: missing,
  };
}

export function parseMissingRequiredDocumentSlots(
  payload: Record<string, unknown> | undefined,
): MissingRequiredDocumentSlot[] {
  if (!payload || !Array.isArray(payload.missing_slots)) return [];
  const out: MissingRequiredDocumentSlot[] = [];
  for (const row of payload.missing_slots) {
    if (!row || typeof row !== "object") continue;
    const slotKey = (row as { slot_key?: unknown }).slot_key;
    const label = (row as { label?: unknown }).label;
    if (typeof slotKey === "string" && typeof label === "string") {
      out.push({ slot_key: slotKey, label });
    }
  }
  return out;
}
